<template>
  <section
    v-if="!loader"
    class="app-ecommerce-details"
  >
    <!-- Content -->
    <b-row class="match-height">
      <b-col
        lg="8"
      >
        <b-alert
          v-if="account.notification"
          :variant="account.notification.type"
          show
        >
          <h4 class="alert-heading">
            <feather-icon
              size="18"
              icon="AlertTriangleIcon"
              class="mr-50"
            />
            <span>{{ account.notification.heading }}</span>
          </h4>
          <div class="alert-body">
            <p
              v-for="(value,key) in account.notification.body"
              :key="key"
            >
              {{ value }}
            </p>
          </div>
        </b-alert>
        <b-card>
          <b-card-body>
            <b-row>
              <b-col md="6">
                <!-- account Name -->
                <h4 v-if="!renameAccount">{{ account.name }}
                  <b-button
                    variant="gradient-warning"
                    class="btn-icon"
                    size="sm"
                    @click="renameAccount = true"
                  >
                    <feather-icon icon="EditIcon" />
                  </b-button>
                </h4>
                <b-row v-else>
                  <b-col
                    lg="12"
                  >
                    <!-- button on right -->
                    <b-input-group>
                      <b-input-group-prepend>
                        <b-button
                          variant="outline-primary"
                          :disabled="spin"
                          @click="renameAccount = false"
                        >
                          {{ $t('Cancel') }}
                        </b-button>
                      </b-input-group-prepend>
                      <b-form-input
                        v-model="newAccountName"
                        :placeholder="$t('Account name')"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-primary"
                          :disabled="spin"
                          @click="updateAccount"
                        >
                          {{ $t('Save') }}
                          <b-spinner
                            v-if="spin"
                            small
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-col>
                </b-row>

                <!-- Status -->
                <b-card-text class="mt-2">{{ $t("Status") }} :
                  <b-badge
                    :variant="alertPaymentColor"
                    class="badge-glow"
                  >
                    {{ account.display_status }}
                  </b-badge>
                </b-card-text>
                <b-card-text v-if="!isConnecting && account.is_active" />
              </b-col>
              <b-col md="6">
                <token-input
                  :id="account.id"
                  :token="account.api_token"
                />
              </b-col>
            </b-row>
            <b-row class="mt-5">
              <b-col
                v-for="(item,key) in statistics"
                :key="key"
                xl="3"
                sm="6"
                :class="item.customClass"
              >
                <b-media no-body>
                  <b-media-aside

                    class="mr-2"
                  >
                    <b-avatar
                      size="48"
                      :variant="item.color"
                    >
                      <feather-icon
                        size="24"
                        :icon="item.icon"
                      />
                    </b-avatar>
                  </b-media-aside>
                  <b-media-body class="my-auto">
                    <h4 class="font-weight-bolder mb-0">
                      {{ item.title }}
                    </h4>
                    <b-card-text class="font-small-3 mb-0">
                      {{ item.subtitle }}
                    </b-card-text>
                  </b-media-body>
                </b-media>
              </b-col>
              <b-col
                v-if="isConnecting"
                xl="3"
                sm="6"
              >
                <b-button
                  class="btn btn-info"
                  @click="$bvModal.show('accounts-send-unsent-messages-modal')"
                >
                  {{ $t('Account send unsent messages') }}
                </b-button>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        v-if="account.is_active"
        lg="4"
      >
        <b-card>
          <b-card-body>
            <div
              v-if="isConnecting"
              class="d-flex flex-column align-items-center justify-content-center"
            >
              <h3 class="text-success mb-2">
                {{ $t('You are connected') }}!
              </h3>
              <b-avatar
                class="mb-2"
                size="160"
                :src="connection.me.profile"
              />
              <p class="text-info">
                {{ connection.me.name }}
              </p>
              <p
                class="text-info"
                style="direction: ltr"
              >
                {{ connection.me.phone }} {{ connection.me.username }}
              </p>
              <b-button
                v-if="$can('update', 'accounts') && account.has_screenshot"
                :disabled="screenshotSpin"
                class="btn btn-info"
                @click="takeScreenshot"
              >
                <feather-icon icon="CameraIcon" />
                {{ $t('Account screenshot') }}
                <b-spinner
                  v-if="screenshotSpin"
                  small
                />
              </b-button>
              <div
                v-if="account.restart_able"
                class="d-flex flex-column align-items-center justify-content-center"
              >
                <b-alert
                  class="my-2"
                  variant="info"
                  show
                >
                  <div class="alert-body">
                    <feather-icon
                      icon="InfoIcon"
                      class="mr-50"
                    />
                    {{
                      $t('Restart alert')
                    }}
                  </div>
                </b-alert>
                <b-button
                  variant="gradient-warning"
                  :disabled="restartSpin"
                  @click="restart"
                >
                  <feather-icon icon="RefreshCwIcon" />
                  {{ $t('Restart') }}!
                  <b-spinner
                    v-if="restartSpin"
                    small
                  />
                </b-button>
              </div>
              <b-button
                v-if="account.logout_able"
                class="mt-2"
                variant="gradient-danger"
                :disabled="logoutSpin"
                @click="logout"
              >
                <feather-icon icon="LogOutIcon" />
                {{ $t('Logout') }}!
                <b-spinner
                  v-if="logoutSpin"
                  small
                />
              </b-button>
              <accounts-send-unset-messages-modal :id="account.id" />
            </div>
            <div
              v-else
            >
              <div
                v-if="!connection"
                class="d-flex align-items-center justify-content-center"
              >
                <b-alert
                  class="my-2"
                  variant="danger"
                  show
                >
                  <div class="alert-body">
                    <feather-icon
                      icon="InfoIcon"
                      class="mr-50"
                    />
                    {{
                      $t("Receiving account connection")
                    }}
                  </div>
                </b-alert>
              </div>
              <div v-else>
                <div
                  v-if="connection.needRestart"
                  class="d-flex flex-column align-items-center justify-content-center"
                >
                  <h3 class="text-danger mb-2">
                    {{ $t('Your connection is not established') }}!
                  </h3>
                  <p class="text-warning">
                    {{ $t('Click the Restart button to reconnect') }}
                  </p>
                  <b-spinner
                    v-if="restartSpin"
                  />
                  <statistic-card-vertical
                    v-else
                    icon="RefreshCcwIcon"
                    :statistic-title="$t('Restart')"
                    color="success"
                    class="cursor-pointer"
                    :click="restart"
                    statistic=""
                  />
                </div>
                <div v-else>
                  <account-connector
                    :connection="connection"
                    :account="account"
                    :restart-spin="restartSpin"
                    @onConnectedStart="clearGetConnectionInterval"
                    @onQrError="needRestart"
                    @onAccountConnected="getConnection"
                  />
                  <div
                    v-if="account.restart_able"
                    class="d-flex flex-column align-items-center justify-content-center"
                  >
                    <b-alert
                      class="my-2"
                      variant="info"
                      show
                    >
                      <div class="alert-body">
                        <feather-icon
                          icon="InfoIcon"
                          class="mr-50"
                        />
                        {{
                          $t('Restart alert')
                        }}
                      </div>
                    </b-alert>
                    <b-button
                      variant="gradient-warning"
                      :disabled="restartSpin"
                      @click="restart"
                    >
                      <feather-icon icon="RefreshCwIcon" />
                      {{ $t('Restart') }}!
                      <b-spinner
                        v-if="restartSpin"
                        small
                      />
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    <b-row
      class="match-height"
    >
      <b-col
        v-if="isConnecting"
        :lg="account.receiving_message ? 6 : 8"
      >
        <b-card>
          <b-card-body>
            <send-test-message
              :account="account"
              @onStartSend="clearGetConnectionInterval"
              @onEndSend="setGetConnectionInterval"
            />
          </b-card-body>
        </b-card>
      </b-col>
      <b-col
        v-if="account.is_active && account.receiving_message"
        lg="6"
      >
        <b-card>
          <b-card-body>
            <webhook-form :account="account" />
          </b-card-body>
        </b-card>
      </b-col>
      <b-col cols="12">
        <messages-index />
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BRow, BCol,
  BCard, BCardBody, BCardText, BMedia, BMediaAside, BAvatar, BMediaBody, BButton, BBadge, BAlert,
  BSpinner,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import StatisticCardVertical from '@core/components/statistics-cards/StatisticCardVertical.vue'
import SendTestMessage from '../../components/accounts/SendTestMessage'
import WebhookForm from '../../components/accounts/Webhookform'
import TokenInput from '../../components/accounts/TokenInput'
import MessagesIndex from '../messages/Index'
import AccountConnector from '../../components/accounts/show/connectors/AccountConnector.vue'
import AccountsSendUnsetMessagesModal from '../../components/accounts/show/AccountsSendUnsetMessagesModal.vue'

export default {
  components: {
    AccountsSendUnsetMessagesModal,
    AccountConnector,
    WebhookForm,
    MessagesIndex,
    StatisticCardVertical,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardText,
    BSpinner,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BMedia,
    BMediaAside,
    BAvatar,
    BMediaBody,
    BButton,
    BBadge,
    SendTestMessage,
    TokenInput,
    BAlert,
  },
  directives: {
    Ripple,
  },
  props: {
    id: {
      type: [String, Number],
      default: () => '',
    },
  },
  data() {
    return {
      loader: true,
      spin: false,
      restartSpin: false,
      screenshotSpin: false,
      logoutSpin: false,
      interval: null,
      account: {
        panel: {},
      },
      connection: {},
      renameAccount: false,
      newAccountName: null,
      statistics: [
        {
          icon: 'MessageSquareIcon',
          color: 'light-info',
          title: '0',
          subtitle: this.$t('Total Messages'),
          customClass: 'mb-2 mb-xl-0',
        },
        {
          icon: 'MessageCircleIcon',
          color: 'light-success',
          title: '0',
          subtitle: this.$t('Today Messages'),
          customClass: 'mb-2 mb-sm-0',
        },
        {
          icon: 'ClockIcon',
          color: 'light-danger',
          title: '0',
          subtitle: this.$t('In queue Messages'),
          customClass: 'mb-2 mb-sm-0',
        },
      ],
    }
  },
  computed: {
    isConnecting() {
      return this.connection && this.connection.isConnected
    },
    alertPaymentColor() {
      if (this.account.status === 'suspended') return 'danger'
      if (this.account.is_active) return 'success'
      return 'warning'
    },
  },
  created() {
    this.getData()
    this.getStatistic()
  },
  beforeDestroy() {
    this.clearGetConnectionInterval()
  },
  methods: {
    getData() {
      this.loader = true
      this.$http
        .get(`accounts/${this.id}`)
        .then(({ data }) => {
          this.account = data
          this.connection = this.account.connection
          this.restartSpin = false
          if (this.newAccountName === null) {
            this.newAccountName = this.account.name
          }
          this.loader = false
          this.getConnection()
        })
        .catch(() => {
          this.loader = false
        })
    },
    getConnection() {
      this.clearGetConnectionInterval()
      this.$http
        .get(`accounts/${this.id}/connection`)
        .then(({ data }) => {
          this.connection = data
          this.setGetConnectionInterval()
        })
        .catch(() => {
          this.setGetConnectionInterval()
        })
    },
    needRestart() {
      this.connection.needRestart = true
    },
    getStatistic() {
      this.$http.get(`accounts/${this.id}/statistic`).then(({ data }) => {
        this.statistics[0].title = data.total_message
        this.statistics[1].title = data.today_message
        this.statistics[2].title = data.total_message_queue
      })
    },
    updateAccount() {
      this.spin = true
      this.$http.put(`accounts/${this.id}`, {
        name: this.newAccountName,
      }).then(() => {
        this.account.name = this.newAccountName
        this.spin = false
        this.renameAccount = false
      }).catch(() => {
        this.spin = false
      })
    },
    setGetConnectionInterval() {
      if (this.interval === null) {
        this.interval = setInterval(() => {
          this.getConnection()
        }, (this.isConnecting ? 60 : 20) * 1000)
      }
    },
    clearGetConnectionInterval() {
      if (this.interval !== null) {
        clearInterval(this.interval)
        this.interval = null
      }
    },
    takeScreenshot() {
      this.screenshotSpin = true
      this.$http.get(`accounts/${this.account.id}/screenshot`).then(({ data }) => {
        window.open(data, '_blank').focus()
        this.screenshotSpin = false
      }).catch(() => {
        this.screenshotSpin = false
      })
    },
    logout() {
      this.clearGetConnectionInterval()
      this.logoutSpin = true
      this.$http.put(`accounts/${this.id}/logout`).then(({ data }) => {
        this.account = data
        this.logoutSpin = false
        this.setGetConnectionInterval()
      }).catch(() => {
        this.logoutSpin = false
        this.setGetConnectionInterval()
      })
    },
    restart() {
      this.clearGetConnectionInterval()
      this.restartSpin = true
      this.$http.put(`accounts/${this.id}/restart`).then(({ data }) => {
        this.restartSpin = false
        this.connection = data
      }).catch(() => {
        this.restartSpin = false
      })
    },
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/pages/app-ecommerce-details.scss";
</style>
